import React from 'react';
import { CalendarIcon, GlobeAmericasIcon, ClockIcon, ChartBarIcon } from '@heroicons/react/24/solid';
const StatCard = ({ icon: Icon, title, value, subtitle, className, loading = false }) => {
  const isDark = className.includes('text-white');
  const bar = isDark ? 'bg-white/25' : 'bg-gray-400/35';

  return (
    <div className={`bg-white rounded-xl shadow p-2 flex-1 ${className}`}>
      <div className="flex items-start justify-between h-full">
        <div className="flex-1 flex flex-col justify-between py-0.5">
          <div>
            <div className="flex items-center gap-1.5">
              <Icon className={`w-4 h-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} />
              <h3 className={`text-[11px] font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>{title}</h3>
            </div>
            {loading ? (
              <div className={`h-[1.4rem] w-28 rounded mt-1 animate-pulse ${bar}`} />
            ) : (
              <div className={`text-lg font-bold leading-tight mt-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>{value}</div>
            )}
          </div>
          {loading ? (
            <div className={`h-4 w-36 rounded mt-1.5 animate-pulse ${bar}`} />
          ) : subtitle && (
            <p className={`text-xs font-semibold line-clamp-1 mt-1.5 ${isDark ? 'text-gray-200' : 'text-gray-600'}`}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const CardStats = ({ travelStats, isTripsLoading }) => {
  // ##### Format date to a more readable format
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No info yet';
    
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'No info yet';
    }
  };

  // ##### Format numeric values with fallback
  const formatNumber = (value, fallback = '-') => {
    if (value === null || value === undefined || value === 0) return fallback;
    return value.toString();
  };

  // ##### Show loading state
  if (isTripsLoading) {
    return (
      <div className="flex flex-col gap-1.5 flex-1">
        <StatCard
          icon={GlobeAmericasIcon}
          title="Overall Trips"
          loading
          className="bg-gradient-to-br from-gray-800 to-gray-700 border border-gray-600 text-white"
        />
        <StatCard
          icon={ChartBarIcon}
          title="Statistics"
          loading
          className="bg-gradient-to-br from-gray-200 to-gray-100 border border-gray-300"
        />
        <StatCard
          icon={CalendarIcon}
          title="This Year"
          loading
          className="bg-gradient-to-br from-gray-100 to-gray-50 border border-gray-200"
        />
        <StatCard
          icon={ClockIcon}
          title="Next Trip"
          loading
          className="bg-white border border-gray-100"
        />
      </div>
    );
  }

  // ##### Show fallback when no data
  if (!travelStats || travelStats.totalTrips === 0) {
    return (
      <div className="flex flex-col gap-1.5 flex-1">
        <StatCard
          icon={GlobeAmericasIcon}
          title="Overall Trips"
          value="No trips yet"
          subtitle="Start by adding some trips"
          className="bg-gradient-to-br from-gray-800 to-gray-700 border border-gray-600 text-white"
        />
        <StatCard
          icon={ChartBarIcon}
          title="Statistics"
          value="No data"
          subtitle="Add trips to see statistics"
          className="bg-gradient-to-br from-gray-200 to-gray-100 border border-gray-300"
        />
        <StatCard
          icon={CalendarIcon}
          title="This Year"
          value="No trips"
          subtitle="No trips planned for this year"
          className="bg-gradient-to-br from-gray-100 to-gray-50 border border-gray-200"
        />
        <StatCard
          icon={ClockIcon}
          title="Next Trip"
          value="No info yet"
          subtitle="No upcoming trips planned"
          className="bg-white border border-gray-100"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 flex-1">
      {/* Overall Stats Card - Primeiro (mais escuro) */}
      <StatCard
        icon={GlobeAmericasIcon}
        title="Overall Trips"
        value={`${formatNumber(travelStats.totalCountries)} Countries`}
        subtitle={`${formatNumber(travelStats.totalTrips)} total trips`}
        className="bg-gradient-to-br from-gray-800 to-gray-700 border border-gray-600 text-white"
      />

      {/* Statistics Card - Segundo (médio-escuro) */}
      <StatCard
        icon={ChartBarIcon}
        title="Statistics"
        value={`${formatNumber(travelStats.totalDaysTraveling)} Days Traveling`}
        subtitle={`~${formatNumber(travelStats.meanDuration)} days mean duration`}
        className="bg-gradient-to-br from-gray-200 to-gray-100 border border-gray-300"
      />

      {/* This Year Stats Card - Terceiro (médio-claro) */}
      <StatCard
        icon={CalendarIcon}
        title="This Year"
        value={`${formatNumber(travelStats.yearTrips)} Trips`}
        subtitle={`Last: ${formatDate(travelStats.lastTrip.date)} - ${travelStats.lastTrip.title}`}
        className="bg-gradient-to-br from-gray-100 to-gray-50 border border-gray-200"
      />

      {/* Next Trip Card - Quarto (mais claro/branco) */}
      <StatCard
        icon={ClockIcon}
        title="Next Trip"
        value={travelStats.nextTrip.daysUntil > 0 
          ? `${travelStats.nextTrip.daysUntil} Days to go` 
          : 'No upcoming trips'
        }
        subtitle={`${formatDate(travelStats.nextTrip.date)} - ${travelStats.nextTrip.title}`}
        className="bg-white border border-gray-100"
      />
    </div>
  );
};

export default CardStats;
